if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.recipientEmailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.recipientNameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.senderNameInput = this.querySelector(`#Sender-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.characterCounter = this.querySelector('.remaining-characters');
        this.maxCharacters = 500; // Maximum characters for the message
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
        
        // Add form validation
        this.setupFormValidation();
        
        // Setup character counter for message textarea
        this.setupCharacterCounter();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.clearErrorMessage();
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }
      }

      inputFields() {
        return [this.recipientEmailInput, this.recipientNameInput, this.senderNameInput, this.messageInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
        // Reset character counter when fields are cleared
        this.updateCharacterCounter();
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            // Map field names to correct IDs
            let fieldId, errorId;
            if (key === 'email') {
              fieldId = `#Recipient-email-${this.dataset.sectionId}`;
              errorId = `RecipientForm-email-error-${this.dataset.sectionId}`;
            } else if (key === 'name') {
              fieldId = `#Recipient-name-${this.dataset.sectionId}`;
              errorId = `RecipientForm-name-error-${this.dataset.sectionId}`;
            } else if (key === 'sender_name' || key === '__shopify_sender_name' || key === 'Sender name') {
              fieldId = `#Sender-name-${this.dataset.sectionId}`;
              errorId = `RecipientForm-sender_name-error-${this.dataset.sectionId}`;
            } else if (key === 'message') {
              fieldId = `#Recipient-message-${this.dataset.sectionId}`;
              errorId = `RecipientForm-message-error-${this.dataset.sectionId}`;
            } else {
              fieldId = `#Recipient-${key}-${this.dataset.sectionId}`;
              errorId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            }
            
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldId, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            let inputElement;
            if (key === 'email') {
              inputElement = this.recipientEmailInput;
            } else if (key === 'name') {
              inputElement = this.recipientNameInput;
            } else if (key === 'sender_name' || key === '__shopify_sender_name' || key === 'Sender name') {
              inputElement = this.senderNameInput;
            } else if (key === 'message') {
              inputElement = this.messageInput;
            } else {
              inputElement = this[`${key}Input`];
            }
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.recipientEmailInput, this.recipientNameInput, this.senderNameInput, this.messageInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      setupFormValidation() {
        // Find the product form
        this.productForm = this.closest('product-form');
        if (this.productForm && this.productForm.form) {
          this.productForm.form.addEventListener('submit', this.validateBeforeSubmit.bind(this), true);
        }
      }

      validateBeforeSubmit(event) {
        // Only validate if recipient form is active (checkbox checked)
        if (!this.checkboxInput.checked) return;

        const errors = {};
        
        // Validate recipient name
        if (!this.recipientNameInput.value.trim()) {
          errors.name = [window.recipientFormStrings?.recipient_name_required || 'Recipient name is required'];
        }
        
        // Validate recipient email
        if (!this.recipientEmailInput.value.trim()) {
          errors.email = [window.recipientFormStrings?.recipient_email_required || 'Recipient email is required'];
        }
        
        // Validate sender name
        if (!this.senderNameInput.value.trim()) {
          errors.sender_name = [window.recipientFormStrings?.sender_name_required || 'Your name is required'];
        }

        // If we have validation errors, prevent submission and show them
        if (Object.keys(errors).length > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.displayErrorMessage(window.recipientFormStrings?.validation_error_title || 'Please fill in all required fields', errors);
          
          // Reset submit button state
          const submitButton = this.productForm.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.setAttribute('aria-disabled', false);
            submitButton.classList.remove('loading');
            const spinner = this.productForm.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          }
          
          return false;
        } else {
          // Clear any existing errors if validation passes
          this.clearErrorMessage();
        }
      }

      setupCharacterCounter() {
        if (this.messageInput && this.characterCounter) {
          // Set initial character count
          this.updateCharacterCounter();
          
          // Add event listener for input changes
          this.messageInput.addEventListener('input', this.updateCharacterCounter.bind(this));
        }
      }

      updateCharacterCounter() {
        if (this.characterCounter && this.messageInput) {
          const currentLength = this.messageInput.value.length;
          const remainingChars = this.maxCharacters - currentLength;
          
          // Update the character counter display
          this.characterCounter.textContent = remainingChars;
          
          // Add visual feedback when approaching limit
          if (remainingChars <= 50) {
            this.characterCounter.style.color = '#dc2626'; // Red color for warning
          } else if (remainingChars <= 100) {
            this.characterCounter.style.color = '#f59e0b'; // Orange color for caution
          } else {
            this.characterCounter.style.color = ''; // Reset to default color
          }
        }
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}
